import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { IconButton, Icons, SkeletonText } from 'utoniq-ui'

import { ItemDetailBoxFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  item: ItemDetailBoxFragment | null
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ResaleCreatePrecautionTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('resaleCreatePrecaution')
  const { routerPages, router, generatePath, routerNames } = useRouter<{
    itemId: string
  }>()
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleExhibit'))

  return (
    <BaseLayout
      margin
      header={{
        back: true,
        title: t('title'),
        defaultBackPage: generatePath(routerPages.ResaleCreate, {
          itemId: router.query.itemId ?? '#',
        }),
        right: (
          <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
        ),
      }}
    >
      <Head title={routerNames.ResaleCreatePrecaution} />
      {state.loading || !state.item ? (
        <ResalePrecautionSkeleton />
      ) : (
        <>
          <ResaleTitleCell />
          <Notice />
          <PrecautionContentCell item={state.item as ItemDetailBoxFragment} />
          <PrecautionNoteCell />
          <SubmitButtonCell />
        </>
      )}
    </BaseLayout>
  )
}

const ResaleTitleCell: Cell = () => {
  const { t } = useTranslation('resaleCreatePrecaution')
  return (
    <CellFrame sx={{ py: 5 }}>
      <Typography variant="h4">{t('resaleInfo')}</Typography>
    </CellFrame>
  )
}

const ResalePrecautionSkeleton: Cell = () => (
  <CellFrame>
    <SkeletonText variant="h4" width="25%" />
    <Stack
      sx={{
        mt: 6,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: 'grey.100',
        py: 2,
        px: 1,
      }}
    >
      <SkeletonText variant="body2" width="90%" />
      <SkeletonText variant="body2" width="50%" />
    </Stack>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={2} />
    </Stack>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={4} />
    </Stack>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={2} />
    </Stack>
    <Stack sx={{ pt: 9 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="body2" numberOfLine={6} />
    </Stack>
    <Stack alignItems="center" sx={{ pt: 8, pb: 6 }}>
      <SkeletonText variant="caption" width="25%" sx={{ mb: 2 }} />
      <Skeleton
        variant="rectangular"
        height={44}
        width={212}
        sx={{ borderRadius: 34 }}
      />
    </Stack>
  </CellFrame>
)

const Notice: Cell = () => {
  const { t } = useTranslation('resaleCreatePrecaution')
  return (
    <CellFrame
      sx={{
        p: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'info.main',
        mb: 8,
      }}
    >
      <Typography variant="body2" sx={{ color: 'info.main' }}>
        {t('resaleNote')}
      </Typography>
    </CellFrame>
  )
}

const PrecautionContentCell = ({ item }: { item: ItemDetailBoxFragment }) => {
  const { t } = useTranslation('resaleCreatePrecaution')
  const { price } = useMemo(() => getItemSkuPrice(item), [item])
  return (
    <CellFrame>
      <Stack spacing={9}>
        <Stack spacing={3}>
          <Typography
            variant="h6"
            sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}
            color="text.secondary"
          >
            {t('listingFee')}
          </Typography>
          <Stack>
            <Typography variant="body2">
              {t('listingFeeDesc', {
                // チケット代金 * リセール手数料の割合
                listingFee:
                  price *
                  (item.metadata?.ticket?.resaleFee?.feeRatePerTicket || 0),
                feeRatePerTicket:
                  (item.metadata?.ticket?.resaleFee?.feeRatePerTicket || 0) *
                  100,
                bankTransferFeePerOrder:
                  item.metadata?.ticket?.resaleFee?.bankTransferFeePerOrder,
              })}
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={3}>
          <Typography
            variant="h6"
            sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}
            color="text.secondary"
          >
            {t('ticketPayment')}
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">{t('ticketPaymentDesc')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('ticketPaymentNote')}
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={3}>
          <Typography
            variant="h6"
            sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}
            color="text.secondary"
          >
            {t('listingCancel')}
          </Typography>
          <Stack spacing={1}>
            <List dense sx={{ listStyleType: 'disc', pl: 5 }}>
              <ListItem disableGutters sx={{ display: 'list-item' }}>
                <Typography variant="body2">
                  {t('listingCancelDesc1')}
                </Typography>
              </ListItem>
              <ListItem disableGutters sx={{ display: 'list-item' }}>
                <Typography variant="body2">
                  {t('listingCancelDesc2')}
                </Typography>
              </ListItem>
            </List>
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const PrecautionNoteCell: Cell = () => {
  const { t } = useTranslation('resaleCreatePrecaution')

  return (
    <CellFrame sx={{ pt: 9 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ bgcolor: 'grey.50', py: 2, px: 1 }}
      >
        <Icons.Error fontSize="small" sx={{ color: 'grey.600' }} />
        <Typography variant="h6" color="text.secondary">
          {t('note')}
        </Typography>
      </Stack>
      <Stack spacing={1}>
        <List dense sx={{ listStyleType: 'disc', pl: 5 }}>
          {[1, 2, 3, 4, 5, 6].map(item => {
            return (
              <ListItem key={item} disableGutters sx={{ display: 'list-item' }}>
                <Typography variant="body2">{t('noteDesc' + item)}</Typography>
              </ListItem>
            )
          })}
        </List>
      </Stack>
    </CellFrame>
  )
}

const SubmitButtonCell: Cell = () => {
  const { t } = useTranslation('resaleCreatePrecaution')
  const [checked, setChecked] = useState<boolean>(false)
  const { generatePath, routerPages, router } =
    useRouter<{ itemId: string; serialIds: string; skuId?: string }>()

  return (
    <CellFrame>
      <Stack spacing={2} alignItems="center" sx={{ pt: 3, pb: 7 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onClick={() => setChecked(prevState => !prevState)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {t('checkBoxLabel')}
            </Typography>
          }
        />
        <Button
          size="large"
          disabled={!checked}
          sx={{ width: 212 }}
          onClick={() =>
            router.push({
              pathname: generatePath(routerPages.ResaleCreateConfirmation, {
                itemId: router.query.itemId,
              }),
              search: `?serialIds=${router.query.serialIds}${
                router.query?.skuId ? `&skuId=${router.query?.skuId}` : ''
              }`,
            })
          }
        >
          {t('submit')}
        </Button>
        <Box sx={{ pt: 3 }}>
          <Button
            variant="text"
            size="small"
            sx={{ width: 212, display: 'block' }}
            onClick={() =>
              router.goBack(
                generatePath(routerPages.ResaleCreate, {
                  itemId: router.query.itemId,
                })
              )
            }
          >
            {t('back')}
          </Button>
        </Box>
      </Stack>
    </CellFrame>
  )
}
